// Route shape, waypoint, and interception return path builders — pure functions.

import { snapToPixel, lerp, clampRange } from '../util/math.js';
import { fieldMetrics, orientX, screenXForYard, clampAdvanceX } from '../field/metrics.js';
import { compactPathPoints } from './track.js';
import { playHash, turnoverReturnYards, stopReasonForPlay } from './helpers.js';
import type { Point } from '../util/interpolation.js';

function resolvedPassFamily(play: any): string {
  if (play.passFamily) {
    return play.passFamily;
  }
  const legacy: Record<string, string> = {
    'quick-slant': 'quick',
    'mesh-cross': 'mesh',
    'play-action-post': 'shot',
    'flood-shot': 'flood',
    'screen-left': 'screen',
    'hail-mary': 'hail-mary',
  };
  return legacy[play.playId] || '';
}

function resolvedRunFamily(play: any): string {
  if (play.runFamily) {
    return play.runFamily;
  }
  const legacy: Record<string, string> = {
    'inside-zone': 'zone',
    'power-o': 'power',
    'counter-tray': 'counter',
    'toss-sweep': 'toss',
    'zone-read': 'option',
    'qb-draw': 'draw',
    'qb-sneak': 'sneak',
  };
  return legacy[play.playId] || '';
}

function visualProfile(play: any): any {
  return play?.visualProfile || {};
}

function profileNumber(play: any, key: string, fallback: number, min: number, max: number): number {
  const value = Number(visualProfile(play)?.[key]);
  return Number.isFinite(value) ? clampRange(value, min, max) : fallback;
}

function resolvedRouteStyle(play: any): string {
  return visualProfile(play).routeStyle || resolvedPassFamily(play);
}

export function routeMotionSample(play: any, role: string): 'polyline' | 'spline' {
  const routeStyle = resolvedRouteStyle(play);
  const playId = play?.playId || '';

  if (routeStyle === 'quick-break' || routeStyle === 'settled-breaker') {
    return 'polyline';
  }

  if ([
    'inside-window',
    'sideline-window',
    'stride-catch',
    'layered-open',
    'contested',
  ].includes(routeStyle)) {
    return 'spline';
  }

  if (routeStyle === 'checkdown') {
    return role === 'RB' || role === 'TE' ? 'spline' : 'polyline';
  }

  if ([
    'mesh-cross',
    'play-action-post',
    'flood-shot',
    'screen-left',
    'wr-bubble-screen',
    'te-delay-screen',
    'hail-mary',
  ].includes(playId)) {
    return 'spline';
  }

  return 'polyline';
}

export function buildRouteShapePoints(play: any, role: string, startPoint: Point, targetPoint: Point, losX: number, centerY: number, direction: number): Point[] {
  const m = fieldMetrics();
  const profile = visualProfile(play);
  const routeStyle = resolvedRouteStyle(play);
  const stemDepthScale = profileNumber(play, 'stemDepthScale', 1, 0.82, 1.18);
  const routeSpacingScale = profileNumber(play, 'routeSpacingScale', 1, 0.84, 1.2);
  const breakLean = profileNumber(play, 'breakLean', 0.42, 0.26, 0.7);
  const breakYOffset = profileNumber(play, 'breakYOffset', 0, -24, 24);
  const laneBend = profileNumber(play, 'laneBend', 0, -22, 22);
  const catchStrideScale = profileNumber(play, 'catchStrideScale', 1, 0.84, 1.18);
  const extendedMode = !!profile.extendedMode;
  const targetY = clampRange(
    snapToPixel(targetPoint.y + (extendedMode ? laneBend * 0.18 : laneBend * 0.08)),
    m.fieldTop + 16,
    m.fieldBottom - 16
  );
  const stemShort = orientX(losX, (routeStyle === 'screen' ? 16 : routeStyle === 'stick' ? 20 : 24) * stemDepthScale, direction);
  const stemMedium = orientX(losX, (routeStyle === 'spacing' ? 42 : routeStyle === 'crossers' ? 48 : 54) * stemDepthScale * routeSpacingScale, direction);
  const stemDeep = orientX(losX, (routeStyle === 'vertical' ? 96 : 88) * stemDepthScale, direction);
  const stemBomb = orientX(losX, (routeStyle === 'vertical' ? 138 : 128) * stemDepthScale, direction);
  const bendY = clampRange(snapToPixel(lerp(startPoint.y, targetY, breakLean) + breakYOffset), m.fieldTop + 16, m.fieldBottom - 16);
  const shoulderY = clampRange(snapToPixel(lerp(startPoint.y, targetY, 0.68) + breakYOffset * 0.35), m.fieldTop + 16, m.fieldBottom - 16);
  const laneMidY = clampRange(snapToPixel(lerp(startPoint.y, targetY, 0.56) + laneBend * 0.24), m.fieldTop + 16, m.fieldBottom - 16);
  const adjustedTarget = { x: targetPoint.x, y: targetY };
  const passFamily = resolvedPassFamily(play);

  switch (routeStyle) {
    case 'quick-break':
      if (['WR1', 'WR2', 'SLOT'].includes(role)) {
        return compactPathPoints([startPoint, { x: stemShort, y: startPoint.y }, { x: orientX(stemShort, 8, direction), y: bendY }, adjustedTarget]);
      }
      if (role === 'TE') {
        return compactPathPoints([startPoint, { x: orientX(losX, 18 * stemDepthScale, direction), y: startPoint.y }, { x: orientX(losX, 24 * stemDepthScale, direction), y: snapToPixel(startPoint.y - 4) }, adjustedTarget]);
      }
      if (role === 'RB') {
        return compactPathPoints([startPoint, { x: orientX(losX, 8, direction), y: snapToPixel(startPoint.y + 6) }, adjustedTarget]);
      }
      break;
    case 'settled-breaker': {
      const settleX = role === 'TE'
        ? orientX(losX, 22 * stemDepthScale, direction)
        : orientX(losX, 34 * stemDepthScale * routeSpacingScale, direction);
      if (['WR1', 'WR2', 'SLOT', 'TE'].includes(role)) {
        return compactPathPoints([startPoint, { x: settleX, y: startPoint.y }, { x: orientX(settleX, 6, direction), y: snapToPixel(lerp(startPoint.y, targetY, 0.34) + breakYOffset * 0.4) }, adjustedTarget]);
      }
      if (role === 'RB') {
        return compactPathPoints([startPoint, { x: orientX(losX, 10, direction), y: snapToPixel(startPoint.y + 6) }, adjustedTarget]);
      }
      break;
    }
    case 'inside-window':
      if (['WR1', 'WR2', 'SLOT', 'TE'].includes(role)) {
        return compactPathPoints([startPoint, { x: stemMedium, y: startPoint.y }, { x: orientX(stemMedium, 12, direction), y: laneMidY }, adjustedTarget]);
      }
      if (role === 'RB') {
        return compactPathPoints([startPoint, { x: orientX(losX, 10, direction), y: snapToPixel(startPoint.y + 4) }, adjustedTarget]);
      }
      break;
    case 'sideline-window':
      if (['WR1', 'WR2', 'SLOT'].includes(role)) {
        return compactPathPoints([startPoint, { x: stemMedium, y: startPoint.y }, { x: orientX(adjustedTarget.x, -12, direction), y: shoulderY }, adjustedTarget]);
      }
      if (role === 'TE') {
        return compactPathPoints([startPoint, { x: orientX(losX, 34 * stemDepthScale, direction), y: startPoint.y }, { x: orientX(losX, 48 * stemDepthScale, direction), y: laneMidY }, adjustedTarget]);
      }
      if (role === 'RB') {
        return compactPathPoints([startPoint, { x: orientX(losX, 12, direction), y: snapToPixel(startPoint.y + 8) }, adjustedTarget]);
      }
      break;
    case 'checkdown':
      if (role === 'RB' || role === 'TE') {
        return compactPathPoints([startPoint, { x: orientX(losX, 10, direction), y: snapToPixel(startPoint.y + 6) }, { x: orientX(losX, 18, direction), y: laneMidY }, adjustedTarget]);
      }
      if (['WR1', 'WR2', 'SLOT'].includes(role)) {
        return compactPathPoints([startPoint, { x: stemMedium, y: startPoint.y }, { x: orientX(stemMedium, 10, direction), y: shoulderY }, adjustedTarget]);
      }
      break;
    case 'stride-catch':
      if (['WR1', 'WR2', 'SLOT', 'TE'].includes(role)) {
        const strideX = orientX(adjustedTarget.x, -Math.round(18 * catchStrideScale), direction);
        return compactPathPoints([startPoint, { x: stemDeep, y: startPoint.y }, { x: strideX, y: laneMidY }, adjustedTarget]);
      }
      if (role === 'RB') {
        return compactPathPoints([startPoint, { x: orientX(losX, 14, direction), y: startPoint.y + 6 }, adjustedTarget]);
      }
      break;
    case 'layered-open':
      if (['WR1', 'WR2', 'SLOT', 'TE'].includes(role)) {
        return compactPathPoints([startPoint, { x: stemMedium, y: startPoint.y }, { x: stemDeep, y: laneMidY }, { x: orientX(adjustedTarget.x, -20, direction), y: shoulderY }, adjustedTarget]);
      }
      if (role === 'RB') {
        return compactPathPoints([startPoint, { x: orientX(losX, 10, direction), y: startPoint.y + 6 }, adjustedTarget]);
      }
      break;
    case 'contested':
      if (['WR1', 'WR2', 'SLOT', 'TE'].includes(role)) {
        return compactPathPoints([startPoint, { x: stemDeep, y: startPoint.y }, { x: orientX(adjustedTarget.x, -8, direction), y: bendY }, adjustedTarget]);
      }
      if (role === 'RB') {
        return compactPathPoints([startPoint, { x: orientX(losX, 6, direction), y: startPoint.y + 8 }, adjustedTarget]);
      }
      break;
    default:
      break;
  }

  switch (play.playId) {
    case 'quick-slant':
      if (['WR1', 'WR2', 'SLOT'].includes(role)) {
        return compactPathPoints([startPoint, { x: stemShort, y: startPoint.y }, { x: orientX(stemShort, 12, direction), y: bendY }, adjustedTarget]);
      }
      break;
    case 'slant-flat':
      if (role === 'WR1') {
        return compactPathPoints([startPoint, { x: stemShort, y: startPoint.y }, { x: orientX(stemShort, 12, direction), y: bendY }, adjustedTarget]);
      }
      if (role === 'RB' || role === 'TE') {
        return compactPathPoints([startPoint, { x: orientX(losX, 16 * stemDepthScale, direction), y: startPoint.y }, adjustedTarget]);
      }
      break;
    case 'stick':
      if (role === 'TE') {
        return compactPathPoints([startPoint, { x: orientX(losX, 18 * stemDepthScale, direction), y: startPoint.y }, { x: orientX(losX, 24 * stemDepthScale, direction), y: startPoint.y - 4 }, adjustedTarget]);
      }
      if (role === 'SLOT' || role === 'RB') {
        return compactPathPoints([startPoint, { x: orientX(losX, 10 * stemDepthScale, direction), y: startPoint.y }, adjustedTarget]);
      }
      break;
    case 'spacing':
    case 'red-zone-spacing':
      if (['WR1', 'WR2', 'SLOT', 'TE'].includes(role)) {
        const settleX = role === 'TE' ? orientX(losX, 24 * stemDepthScale, direction) : orientX(losX, 34 * stemDepthScale * routeSpacingScale, direction);
        return compactPathPoints([startPoint, { x: settleX, y: startPoint.y }, adjustedTarget]);
      }
      if (role === 'RB') {
        return compactPathPoints([startPoint, { x: orientX(losX, -10, direction), y: startPoint.y + 6 }, adjustedTarget]);
      }
      break;
    case 'mesh-cross':
      if (role === 'SLOT') return compactPathPoints([startPoint, { x: stemShort, y: startPoint.y }, { x: stemMedium, y: clampRange(centerY + 22 + laneBend * 0.3, m.fieldTop + 18, m.fieldBottom - 18) }, adjustedTarget]);
      if (role === 'WR1') return compactPathPoints([startPoint, { x: stemMedium, y: snapToPixel(lerp(startPoint.y, targetY, 0.3)) }, adjustedTarget]);
      if (role === 'WR2') return compactPathPoints([startPoint, { x: stemShort, y: bendY }, adjustedTarget]);
      break;
    case 'play-action-post':
      if (role === 'WR1') return compactPathPoints([startPoint, { x: stemDeep, y: startPoint.y }, { x: orientX(adjustedTarget.x, -18, direction), y: bendY }, adjustedTarget]);
      if (role === 'RB') return compactPathPoints([startPoint, { x: orientX(losX, -28, direction), y: startPoint.y + 10 }, { x: orientX(losX, 10, direction), y: startPoint.y + 6 }, adjustedTarget]);
      break;
    case 'flood-shot':
      if (role === 'WR2') return compactPathPoints([startPoint, { x: stemDeep, y: startPoint.y }, { x: orientX(adjustedTarget.x, -12, direction), y: bendY }, adjustedTarget]);
      if (role === 'WR1') return compactPathPoints([startPoint, { x: stemMedium, y: startPoint.y }, adjustedTarget]);
      break;
    case 'screen-left':
      if (role === 'RB') return compactPathPoints([startPoint, { x: orientX(losX, -28, direction), y: startPoint.y + 8 }, { x: orientX(losX, 10, direction), y: clampRange(centerY + 42 + laneBend * 0.4, m.fieldTop + 18, m.fieldBottom - 18) }, adjustedTarget]);
      break;
    case 'wr-bubble-screen':
      if (role === 'SLOT' || role === 'WR2') {
        return compactPathPoints([startPoint, { x: orientX(losX, 10, direction), y: startPoint.y }, { x: orientX(losX, 18, direction), y: adjustedTarget.y }, adjustedTarget]);
      }
      break;
    case 'te-delay-screen':
      if (role === 'TE') {
        return compactPathPoints([startPoint, { x: orientX(losX, 8, direction), y: startPoint.y }, { x: orientX(losX, -10, direction), y: startPoint.y + 8 }, { x: orientX(losX, 16, direction), y: adjustedTarget.y }, adjustedTarget]);
      }
      break;
    case 'hail-mary':
      if (['WR1', 'WR2', 'SLOT'].includes(role)) return compactPathPoints([startPoint, { x: stemBomb, y: startPoint.y }, { x: orientX(adjustedTarget.x, -20, direction), y: bendY }, adjustedTarget]);
      break;
  }

  switch (passFamily) {
    case 'quick':
      if (['WR1', 'WR2', 'SLOT'].includes(role)) {
        return compactPathPoints([startPoint, { x: stemShort, y: startPoint.y }, { x: orientX(stemShort, 10, direction), y: bendY }, adjustedTarget]);
      }
      if (role === 'TE') {
        return compactPathPoints([startPoint, { x: orientX(losX, 22 * stemDepthScale, direction), y: startPoint.y }, adjustedTarget]);
      }
      break;
    case 'drive':
    case 'levels':
    case 'dig':
      if (['WR1', 'SLOT', 'TE'].includes(role)) {
        return compactPathPoints([startPoint, { x: stemMedium, y: startPoint.y }, { x: orientX(stemMedium, 14, direction), y: bendY }, adjustedTarget]);
      }
      break;
    case 'smash':
      if (['WR1', 'WR2'].includes(role)) {
        return compactPathPoints([startPoint, { x: stemShort, y: startPoint.y }, adjustedTarget]);
      }
      if (role === 'TE') {
        return compactPathPoints([startPoint, { x: stemMedium, y: startPoint.y }, adjustedTarget]);
      }
      break;
    case 'screen':
      if (role === 'RB' || role === 'TE' || role === 'SLOT') {
        return compactPathPoints([startPoint, { x: orientX(losX, -26, direction), y: startPoint.y + 8 }, { x: orientX(losX, 8, direction), y: adjustedTarget.y }, adjustedTarget]);
      }
      break;
    case 'boot':
    case 'flood':
      if (['TE', 'WR2', 'RB'].includes(role)) {
        return compactPathPoints([startPoint, { x: stemMedium, y: startPoint.y }, { x: orientX(adjustedTarget.x, -12, direction), y: bendY }, adjustedTarget]);
      }
      break;
    case 'shot':
    case 'dagger':
      if (['WR1', 'WR2', 'SLOT'].includes(role)) {
        return compactPathPoints([startPoint, { x: stemDeep, y: startPoint.y }, { x: orientX(adjustedTarget.x, -18, direction), y: bendY }, adjustedTarget]);
      }
      break;
    case 'red-zone':
      if (role === 'WR1') {
        return compactPathPoints([startPoint, { x: stemMedium, y: startPoint.y }, adjustedTarget]);
      }
      if (role === 'TE') {
        return compactPathPoints([startPoint, { x: stemShort, y: startPoint.y }, adjustedTarget]);
      }
      break;
    case 'hail-mary':
      if (['WR1', 'WR2', 'SLOT'].includes(role)) {
        return compactPathPoints([startPoint, { x: stemBomb, y: startPoint.y }, { x: orientX(adjustedTarget.x, -20, direction), y: bendY }, adjustedTarget]);
      }
      break;
    default:
      break;
  }

  switch (routeStyle) {
    case 'crossers':
      if (['WR1', 'WR2', 'SLOT', 'TE'].includes(role)) {
        return compactPathPoints([startPoint, { x: stemMedium, y: startPoint.y }, { x: orientX(stemMedium, 14, direction), y: bendY }, adjustedTarget]);
      }
      break;
    case 'layered':
    case 'in-breaker':
      if (['WR1', 'SLOT', 'TE'].includes(role)) {
        return compactPathPoints([startPoint, { x: stemMedium, y: startPoint.y }, { x: orientX(stemMedium, 12, direction), y: bendY }, adjustedTarget]);
      }
      break;
    case 'high-low':
      if (['WR1', 'WR2'].includes(role)) {
        return compactPathPoints([startPoint, { x: stemShort, y: startPoint.y }, adjustedTarget]);
      }
      break;
    case 'flood':
      if (['TE', 'WR2', 'RB'].includes(role)) {
        return compactPathPoints([startPoint, { x: stemMedium, y: startPoint.y }, { x: orientX(adjustedTarget.x, -12, direction), y: bendY }, adjustedTarget]);
      }
      break;
    case 'vertical':
      if (['WR1', 'WR2', 'SLOT'].includes(role)) {
        return compactPathPoints([startPoint, { x: stemDeep, y: startPoint.y }, { x: orientX(adjustedTarget.x, -18, direction), y: bendY }, adjustedTarget]);
      }
      break;
    case 'screen':
      if (role === 'RB' || role === 'TE' || role === 'SLOT') {
        return compactPathPoints([startPoint, { x: orientX(losX, -22, direction), y: startPoint.y + 8 }, { x: orientX(losX, 8, direction), y: adjustedTarget.y }, adjustedTarget]);
      }
      break;
    case 'isolation':
      if (role === 'WR1') {
        return compactPathPoints([startPoint, { x: stemMedium, y: startPoint.y }, adjustedTarget]);
      }
      break;
    default:
      break;
  }

  return compactPathPoints([startPoint, { x: stemMedium, y: startPoint.y }, adjustedTarget]);
}

export function buildPostCatchPoints(play: any, catchPoint: Point, endPoint: Point): Point[] {
  const m = fieldMetrics();
  const profile = visualProfile(play);
  const passFamily = resolvedPassFamily(play);
  const catchStyle = String(profile.catchStyle || '');
  const catchStrideScale = profileNumber(play, 'catchStrideScale', 1, 0.84, 1.18);
  const laneBend = profileNumber(play, 'laneBend', 0, -22, 22);
  const extendedMode = !!profile.extendedMode;
  const screenLike = play.playId === 'screen-left' || passFamily === 'screen';
  const leadBlend = screenLike
    ? 0.32
    : catchStyle === 'settled'
      ? 0.24
      : catchStyle === 'in-stride'
        ? 0.46
        : catchStyle === 'high-point'
          ? 0.36
          : 0.5;
  const shoulderBlend = catchStyle === 'settled'
    ? 0.62
    : catchStyle === 'in-stride'
      ? 0.84
      : 0.78;
  const leadX = snapToPixel(lerp(catchPoint.x, endPoint.x, leadBlend * catchStrideScale));
  const leadY = clampRange(snapToPixel(lerp(catchPoint.y, endPoint.y, screenLike ? 0.18 : 0.58) + laneBend * 0.18), m.fieldTop + 18, m.fieldBottom - 18);
  const gatherX = snapToPixel(lerp(catchPoint.x, endPoint.x, screenLike ? 0.16 : catchStyle === 'settled' ? 0.18 : 0.24));
  const gatherY = clampRange(
    snapToPixel(lerp(catchPoint.y, endPoint.y, screenLike ? 0.24 : catchStyle === 'settled' ? 0.18 : 0.28) + laneBend * (screenLike ? 0.2 : 0.14)),
    m.fieldTop + 18,
    m.fieldBottom - 18,
  );
  const shoulderX = snapToPixel(lerp(catchPoint.x, endPoint.x, shoulderBlend));
  const shoulderY = clampRange(snapToPixel(lerp(catchPoint.y, endPoint.y, catchStyle === 'in-stride' ? 0.74 : 0.82) + laneBend * 0.12), m.fieldTop + 18, m.fieldBottom - 18);
  const turnX = snapToPixel(lerp(catchPoint.x, endPoint.x, catchStyle === 'settled' ? 0.7 : 0.76));
  const turnY = clampRange(snapToPixel(lerp(leadY, shoulderY, screenLike ? 0.68 : 0.58) + laneBend * 0.08), m.fieldTop + 18, m.fieldBottom - 18);
  const corridorPoint = extendedMode
    ? {
        x: snapToPixel(lerp(catchPoint.x, endPoint.x, 0.6)),
        y: clampRange(snapToPixel(lerp(leadY, shoulderY, 0.52) + laneBend * 0.1), m.fieldTop + 18, m.fieldBottom - 18),
      }
    : null;
  return compactPathPoints([
    catchPoint,
    { x: gatherX, y: gatherY },
    { x: leadX, y: leadY },
    ...(corridorPoint ? [corridorPoint] : []),
    { x: shoulderX, y: shoulderY },
    { x: turnX, y: turnY },
    endPoint,
  ]);
}

export function interceptionReturnEndPoint(play: any, pickPoint: Point, endX: number, laneY: number, centerY: number): Point {
  const m = fieldMetrics();
  const seed = playHash(play, 'int-return-lane');
  const returnYards = turnoverReturnYards(play);
  const laneBias = pickPoint.y < centerY - 18 ? -1 : pickPoint.y > centerY + 18 ? 1 : seed % 2 === 0 ? -1 : 1;
  const drift = Math.min(60, 12 + returnYards * 0.9);
  return { x: endX, y: clampRange(snapToPixel(lerp(pickPoint.y, laneY, 0.34) + laneBias * drift), m.fieldTop + 20, m.fieldBottom - 20) };
}

export function buildInterceptionReturnPoints(play: any, pickPoint: Point, endPoint: Point, direction: number, centerY: number): Point[] {
  const m = fieldMetrics();
  const returnDirection = -direction;
  const seed = playHash(play, 'int-return-lane');
  const returnYards = turnoverReturnYards(play);
  const laneBias = pickPoint.y < centerY - 18 ? -1 : pickPoint.y > centerY + 18 ? 1 : seed % 2 === 0 ? -1 : 1;
  const gatherPoint = {
    x: orientX(pickPoint.x, 18, returnDirection),
    y: clampRange(snapToPixel(pickPoint.y + laneBias * Math.min(22, 8 + returnYards * 0.24)), m.fieldTop + 18, m.fieldBottom - 18),
  };
  const lanePoint = {
    x: snapToPixel(lerp(pickPoint.x, endPoint.x, 0.34)),
    y: clampRange(snapToPixel(lerp(gatherPoint.y, endPoint.y, 0.28) + laneBias * Math.min(36, 12 + returnYards * 0.44)), m.fieldTop + 18, m.fieldBottom - 18),
  };
  const shoulderPoint = {
    x: snapToPixel(lerp(pickPoint.x, endPoint.x, 0.72)),
    y: clampRange(snapToPixel(lerp(lanePoint.y, endPoint.y, 0.62)), m.fieldTop + 18, m.fieldBottom - 18),
  };
  const turnPoint = {
    x: snapToPixel(lerp(pickPoint.x, endPoint.x, 0.84)),
    y: clampRange(snapToPixel(lerp(shoulderPoint.y, endPoint.y, 0.56) - laneBias * Math.min(12, 4 + returnYards * 0.12)), m.fieldTop + 18, m.fieldBottom - 18),
  };
  return compactPathPoints([pickPoint, gatherPoint, lanePoint, shoulderPoint, turnPoint, endPoint]);
}

export function turnoverDefenderRole(targetRole: string): string {
  if (targetRole === 'WR2') return 'CB2';
  if (targetRole === 'RB') return 'LB3';
  if (targetRole === 'TE') return 'S1';
  if (targetRole === 'SLOT') return 'S2';
  return 'CB1';
}

export function kickoffReturnLaneY(play: any, centerY: number): number {
  const lane = clampRange(Math.round(play.returnLane ?? 0), -2, 2);
  return snapToPixel(centerY + lane * 42);
}

export function kickoffFinishY(play: any, centerY: number): number {
  const m = fieldMetrics();
  const laneY = kickoffReturnLaneY(play, centerY);
  const stopReason = stopReasonForPlay(play);
  if (stopReason === 'out-of-bounds') {
    const lane = clampRange(Math.round(play.returnLane ?? 0), -2, 2);
    const bias = lane === 0 ? (playHash(play, 'kickoff-boundary') % 2 === 0 ? 1 : -1) : Math.sign(lane);
    return clampRange(laneY + bias * 176, m.fieldTop + 20, m.fieldBottom - 20);
  }
  return clampRange(laneY + clampRange(Math.round(play.returnLane ?? 0), -2, 2) * 8, m.fieldTop + 20, m.fieldBottom - 20);
}

export function lossRunLaneY(play: any, handoffY: number, laneY: number): number {
  const laneBlend: Record<string, number> = {
    "inside-zone": 0.24,
    "power-o": 0.3,
    "counter-tray": 0.42,
    "toss-sweep": 0.58,
  };
  const runFamily = resolvedRunFamily(play);
  const familyBlend: Record<string, number> = {
    zone: 0.24,
    power: 0.3,
    duo: 0.28,
    counter: 0.42,
    toss: 0.58,
    draw: 0.3,
    option: 0.44,
    sneak: 0.12,
    boot: 0.5,
  };
  return snapToPixel(lerp(handoffY, laneY, laneBlend[play.playId] ?? familyBlend[runFamily] ?? 0.34));
}

export function buildRunWaypoints(play: any, startX: number, startY: number, handoffX: number, handoffY: number, pressX: number, pressY: number, endX: number, laneY: number, centerY: number, direction: number): Point[] {
  const m = fieldMetrics();
  const runFamily = resolvedRunFamily(play);
  const profile = visualProfile(play);
  const runStyle = profile.runStyle || runFamily;
  const tossSweep = play?.playId === 'toss-sweep';
  const surfaceStretch = profileNumber(play, 'surfaceStretch', 1, 0.84, 1.18);
  const pressPatience = profileNumber(play, 'pressPatience', 1, 0.84, 1.18);
  const pressYOffset = profileNumber(play, 'pressYOffset', 0, -18, 18);
  const laneBend = profileNumber(play, 'laneBend', 0, -22, 22);
  const cutbackAmount = profileNumber(play, 'cutbackAmount', 1, 0.84, 1.22);
  const extendedMode = !!profile.extendedMode;
  const middleY = clampRange(snapToPixel(lerp(handoffY, laneY, 0.42) + laneBend * 0.28), m.fieldTop + 18, m.fieldBottom - 18);
  const outsideY = clampRange(snapToPixel(lerp(centerY, laneY, 0.8) + laneBend * 0.24), m.fieldTop + 18, m.fieldBottom - 18);
  const bentLaneY = clampRange(snapToPixel(laneY + laneBend * (extendedMode ? 0.36 : 0.22)), m.fieldTop + 18, m.fieldBottom - 18);
  const hesitationX = orientX(handoffX, 8 * surfaceStretch, direction);
  const stretchX = orientX(handoffX, 22 * surfaceStretch, direction);
  const lateralX = orientX(handoffX, 34 * surfaceStretch, direction);
  const widenX = orientX(handoffX, 14 * surfaceStretch, direction);
  const arcX = orientX(handoffX, 28 * surfaceStretch, direction);
  const gatherX = orientX(handoffX, 42 * surfaceStretch, direction);
  const patientPressX = snapToPixel(lerp(handoffX, pressX, pressPatience));
  const adjustedPressY = clampRange(snapToPixel(pressY + pressYOffset), m.fieldTop + 18, m.fieldBottom - 18);
  const attackX = orientX(patientPressX, 10 * surfaceStretch, direction);
  const backsideY = snapToPixel(lerp(handoffY, centerY, 0.28));
  const outsideArcY = clampRange(snapToPixel(lerp(handoffY, outsideY, 0.68) + laneBend * 0.16), m.fieldTop + 18, m.fieldBottom - 18);
  const edgeCarryY = clampRange(snapToPixel(lerp(outsideArcY, bentLaneY, 0.16)), m.fieldTop + 18, m.fieldBottom - 18);
  const gatherTurnY = clampRange(snapToPixel(lerp(outsideArcY, bentLaneY, 0.34)), m.fieldTop + 18, m.fieldBottom - 18);
  const turnShoulderY = clampRange(snapToPixel(lerp(outsideArcY, bentLaneY, 0.56)), m.fieldTop + 18, m.fieldBottom - 18);
  const turnX = snapToPixel(lerp(gatherX, endX, 0.42));
  const finishShoulderX = snapToPixel(lerp(turnX, endX, 0.7));
  const finishShoulderY = clampRange(snapToPixel(lerp(turnShoulderY, bentLaneY, 0.64)), m.fieldTop + 18, m.fieldBottom - 18);
  const tossArcShelfX = orientX(handoffX, 20 * surfaceStretch, direction);
  const tossEdgeRideX = orientX(handoffX, 30 * surfaceStretch, direction);
  const tossGatherX = orientX(handoffX, 42 * surfaceStretch, direction);
  const tossLateTurnX = snapToPixel(lerp(tossGatherX, endX, 0.58));
  const tossFinishX = snapToPixel(lerp(tossLateTurnX, endX, 0.78));
  const tossArcLiftY = clampRange(snapToPixel(lerp(handoffY, outsideArcY, 0.3)), m.fieldTop + 18, m.fieldBottom - 18);
  const tossArcShelfY = clampRange(snapToPixel(lerp(handoffY, outsideArcY, 0.72)), m.fieldTop + 18, m.fieldBottom - 18);
  const tossEdgeRideY = clampRange(snapToPixel(lerp(outsideArcY, edgeCarryY, 0.22)), m.fieldTop + 18, m.fieldBottom - 18);
  const tossGatherY = clampRange(snapToPixel(lerp(edgeCarryY, gatherTurnY, 0.26)), m.fieldTop + 18, m.fieldBottom - 18);
  const tossLateTurnY = clampRange(snapToPixel(lerp(edgeCarryY, bentLaneY, 0.46)), m.fieldTop + 18, m.fieldBottom - 18);
  const tossFinishY = clampRange(snapToPixel(lerp(tossLateTurnY, bentLaneY, 0.58)), m.fieldTop + 18, m.fieldBottom - 18);
  const foldX = snapToPixel(lerp(patientPressX, endX, 0.34));
  const foldY = clampRange(snapToPixel(lerp(outsideY, bentLaneY, 0.56)), m.fieldTop + 18, m.fieldBottom - 18);
  const interiorPressY = clampRange(snapToPixel(lerp(adjustedPressY, middleY, 0.24)), m.fieldTop + 18, m.fieldBottom - 18);
  const interiorGatherY = clampRange(snapToPixel(lerp(middleY, bentLaneY, 0.24) + laneBend * 0.08), m.fieldTop + 18, m.fieldBottom - 18);
  const interiorShoulderY = clampRange(snapToPixel(lerp(middleY, bentLaneY, 0.5) + laneBend * 0.05), m.fieldTop + 18, m.fieldBottom - 18);
  const interiorGlideX = snapToPixel(lerp(patientPressX, attackX, 0.48));
  const interiorSettleX = snapToPixel(lerp(attackX, endX, 0.38));
  const interiorFinishX = snapToPixel(lerp(interiorSettleX, endX, 0.74));

  switch (runStyle) {
    case 'crease':
      return compactPathPoints([
        { x: startX, y: startY },
        { x: handoffX, y: handoffY },
        { x: patientPressX, y: interiorPressY },
        { x: interiorGlideX, y: snapToPixel(lerp(interiorPressY, interiorGatherY, 0.52)) },
        { x: attackX, y: interiorGatherY },
        { x: interiorSettleX, y: interiorShoulderY },
        { x: endX, y: bentLaneY },
      ]);
    case 'squeeze':
      return compactPathPoints([
        { x: startX, y: startY },
        { x: handoffX, y: handoffY },
        { x: orientX(handoffX, 10 * surfaceStretch, direction), y: snapToPixel(lerp(handoffY, middleY, 0.18)) },
        { x: patientPressX, y: snapToPixel(lerp(middleY, centerY, 0.18)) },
        { x: interiorSettleX, y: snapToPixel(lerp(middleY, bentLaneY, 0.42)) },
        { x: endX, y: snapToPixel(lerp(bentLaneY, centerY, 0.16)) },
      ]);
    case 'bounce':
      return compactPathPoints([
        { x: startX, y: startY },
        { x: handoffX, y: handoffY },
        { x: stretchX, y: snapToPixel(lerp(handoffY, outsideY, 0.52)) },
        { x: lateralX, y: outsideY },
        { x: orientX(lateralX, 10, direction), y: snapToPixel(lerp(outsideY, bentLaneY, 0.24)) },
        { x: endX, y: bentLaneY },
      ]);
    case 'burst-lane':
      return compactPathPoints([
        { x: startX, y: startY },
        { x: handoffX, y: handoffY },
        { x: patientPressX, y: interiorPressY },
        { x: interiorGlideX, y: snapToPixel(lerp(interiorPressY, interiorGatherY, 0.54)) },
        { x: attackX, y: interiorGatherY },
        { x: interiorSettleX, y: interiorShoulderY },
        { x: endX, y: bentLaneY },
      ]);
    case 'glance':
      return compactPathPoints([
        { x: startX, y: startY },
        { x: handoffX, y: handoffY },
        { x: orientX(handoffX, 12 * surfaceStretch, direction), y: snapToPixel(lerp(handoffY, bentLaneY, 0.22)) },
        { x: patientPressX, y: snapToPixel(lerp(bentLaneY, handoffY, 0.16)) },
        { x: interiorSettleX, y: snapToPixel(lerp(middleY, bentLaneY, 0.46)) },
        { x: endX, y: bentLaneY },
      ]);
    case 'alley':
      return compactPathPoints([
        { x: startX, y: startY },
        { x: handoffX, y: handoffY },
        { x: widenX, y: snapToPixel(lerp(handoffY, outsideArcY, 0.44)) },
        { x: stretchX, y: outsideArcY },
        { x: patientPressX, y: edgeCarryY },
        { x: turnX, y: gatherTurnY },
        { x: finishShoulderX, y: finishShoulderY },
        { x: endX, y: bentLaneY },
      ]);
    case 'sideline':
      if (tossSweep) {
        return compactPathPoints([
          { x: startX, y: startY },
          { x: handoffX, y: handoffY },
          { x: widenX, y: tossArcLiftY },
          { x: tossArcShelfX, y: tossArcShelfY },
          { x: tossEdgeRideX, y: tossEdgeRideY },
          { x: tossGatherX, y: tossGatherY },
          { x: tossLateTurnX, y: tossLateTurnY },
          { x: tossFinishX, y: tossFinishY },
          { x: endX, y: bentLaneY },
        ]);
      }
      return compactPathPoints([
        { x: startX, y: startY },
        { x: handoffX, y: handoffY },
        { x: widenX, y: snapToPixel(lerp(handoffY, outsideArcY, 0.52)) },
        { x: stretchX, y: outsideArcY },
        { x: lateralX, y: edgeCarryY },
        { x: gatherX, y: gatherTurnY },
        { x: turnX, y: turnShoulderY },
        { x: finishShoulderX, y: finishShoulderY },
        { x: endX, y: bentLaneY },
      ]);
    case 'surge':
      return compactPathPoints([
        { x: startX, y: startY },
        { x: handoffX, y: handoffY },
        { x: orientX(handoffX, 12 * surfaceStretch, direction), y: snapToPixel(lerp(handoffY, bentLaneY, 0.18)) },
        { x: interiorSettleX, y: interiorShoulderY },
        { x: endX, y: bentLaneY },
      ]);
    case 'pullers':
    case 'downhill':
    case 'quick-hitter':
      return compactPathPoints([
        { x: startX, y: startY },
        { x: handoffX, y: handoffY },
        { x: patientPressX, y: interiorPressY },
        { x: interiorGlideX, y: snapToPixel(lerp(interiorPressY, interiorGatherY, 0.5)) },
        { x: attackX, y: interiorGatherY },
        { x: interiorFinishX, y: interiorShoulderY },
        { x: endX, y: bentLaneY },
      ]);
    case 'cutback':
      return compactPathPoints([
        { x: startX, y: startY },
        { x: handoffX, y: handoffY },
        { x: orientX(handoffX, 16 * surfaceStretch * cutbackAmount, direction), y: snapToPixel(lerp(handoffY, outsideArcY, 0.28)) },
        { x: orientX(handoffX, 24 * surfaceStretch * cutbackAmount, direction), y: outsideArcY },
        { x: patientPressX, y: snapToPixel(lerp(outsideArcY, bentLaneY, 0.3)) },
        { x: foldX, y: foldY },
        { x: endX, y: bentLaneY },
      ]);
    case 'misdirection':
    case 'backside-leak':
      return compactPathPoints([
        { x: startX, y: startY },
        { x: handoffX, y: handoffY },
        { x: orientX(handoffX, -8, direction), y: backsideY },
        { x: orientX(handoffX, 10 * surfaceStretch, direction), y: snapToPixel(lerp(backsideY, bentLaneY, 0.28)) },
        { x: stretchX, y: snapToPixel(lerp(backsideY, bentLaneY, 0.46)) },
        { x: foldX, y: snapToPixel(lerp(backsideY, bentLaneY, 0.68)) },
        { x: endX, y: bentLaneY },
      ]);
    case 'delayed':
      return compactPathPoints([
        { x: startX, y: startY },
        { x: handoffX, y: handoffY },
        { x: hesitationX, y: snapToPixel(lerp(handoffY, centerY, 0.18)) },
        { x: patientPressX, y: interiorPressY },
        { x: interiorSettleX, y: interiorShoulderY },
        { x: endX, y: bentLaneY },
      ]);
    case 'lateral':
    case 'edge-burst':
    case 'race-edge':
      return compactPathPoints([
        { x: startX, y: startY },
        { x: handoffX, y: handoffY },
        { x: widenX, y: snapToPixel(lerp(handoffY, outsideArcY, 0.48)) },
        { x: arcX, y: outsideArcY },
        { x: lateralX, y: edgeCarryY },
        { x: gatherX, y: gatherTurnY },
        { x: turnX, y: turnShoulderY },
        { x: finishShoulderX, y: finishShoulderY },
        { x: endX, y: bentLaneY },
      ]);
    case 'conflict':
      return compactPathPoints([
        { x: startX, y: startY },
        { x: handoffX, y: handoffY },
        { x: stretchX, y: snapToPixel(lerp(handoffY, bentLaneY, 0.3)) },
        { x: patientPressX, y: snapToPixel(lerp(bentLaneY, outsideY, 0.2)) },
        { x: interiorSettleX, y: interiorShoulderY },
        { x: endX, y: bentLaneY },
      ]);
    default:
      if (runFamily === 'toss' || runFamily === 'option' || runFamily === 'boot') {
        if (tossSweep) {
          return compactPathPoints([
            { x: startX, y: startY },
            { x: handoffX, y: handoffY },
            { x: widenX, y: tossArcLiftY },
            { x: tossArcShelfX, y: tossArcShelfY },
            { x: tossEdgeRideX, y: tossEdgeRideY },
            { x: tossGatherX, y: tossGatherY },
            { x: tossLateTurnX, y: tossLateTurnY },
            { x: tossFinishX, y: tossFinishY },
            { x: endX, y: bentLaneY },
          ]);
        }
        return compactPathPoints([
          { x: startX, y: startY },
          { x: handoffX, y: handoffY },
          { x: widenX, y: snapToPixel(lerp(handoffY, outsideArcY, 0.46)) },
          { x: arcX, y: outsideArcY },
          { x: lateralX, y: edgeCarryY },
          { x: gatherX, y: gatherTurnY },
          { x: turnX, y: turnShoulderY },
          { x: finishShoulderX, y: finishShoulderY },
          { x: endX, y: bentLaneY },
        ]);
      }
      return compactPathPoints([
        { x: startX, y: startY },
        { x: handoffX, y: handoffY },
        { x: patientPressX, y: interiorPressY },
        { x: interiorGlideX, y: snapToPixel(lerp(interiorPressY, interiorGatherY, 0.52)) },
        { x: interiorSettleX, y: interiorShoulderY },
        { x: endX, y: bentLaneY },
      ]);
  }
}

export function buildLossRunWaypoints(play: any, startX: number, startY: number, handoffX: number, handoffY: number, endX: number, endY: number, direction: number): Point[] {
  const runFamily = resolvedRunFamily(play);
  const profile = visualProfile(play);
  const surfaceStretch = profileNumber(play, 'surfaceStretch', 1, 0.84, 1.18);
  const outsideLossY = snapToPixel(lerp(handoffY, endY, 0.68));
  const shortPressX = orientX(handoffX, 10 * surfaceStretch, direction);
  const wideLossX = orientX(handoffX, 18 * surfaceStretch, direction);
  const collapseX = snapToPixel(lerp(shortPressX, endX, 0.48));
  const shortPressY = snapToPixel(lerp(handoffY, endY, 0.26));
  const stumbleX = snapToPixel(lerp(shortPressX, collapseX, 0.46));
  const shoulderLossY = snapToPixel(lerp(handoffY, endY, 0.56));

  if (runFamily === 'toss' || runFamily === 'option' || runFamily === 'boot') {
    return compactPathPoints([
      { x: startX, y: startY },
      { x: handoffX, y: handoffY },
      { x: orientX(handoffX, 8 * surfaceStretch, direction), y: snapToPixel(lerp(handoffY, outsideLossY, 0.42)) },
      { x: wideLossX, y: outsideLossY },
      { x: collapseX, y: snapToPixel(lerp(outsideLossY, endY, 0.56)) },
      { x: endX, y: endY },
    ]);
  }

  if (runFamily === 'counter') {
    return compactPathPoints([
      { x: startX, y: startY },
      { x: handoffX, y: handoffY },
      { x: orientX(handoffX, -8, direction), y: snapToPixel(lerp(handoffY, endY, 0.18)) },
      { x: shortPressX, y: snapToPixel(lerp(handoffY, endY, 0.34)) },
      { x: collapseX, y: snapToPixel(lerp(handoffY, endY, 0.7)) },
      { x: endX, y: endY },
    ]);
  }

  return compactPathPoints([
    { x: startX, y: startY },
    { x: handoffX, y: handoffY },
    { x: shortPressX, y: shortPressY },
    { x: stumbleX, y: shoulderLossY },
    { x: collapseX, y: snapToPixel(lerp(handoffY, endY, 0.66)) },
    { x: endX, y: endY },
  ]);
}

export function liveSequenceCacheKey(context: any): string {
  return [
    'pursuit-model-v2',
    context.presentationPlan?.signature || '',
    context.play.sequence,
    context.play.playId,
    context.play.startYardLine,
    context.play.endYardLine,
    context.play.stopReason || '',
    context.play.kickoffOutcome || '',
    context.play.returnLane ?? '',
    context.play.visualLane ?? '',
    context.play.hangTime ?? '',
    context.play.returnYards ?? '',
    context.play.returnEndYardLine ?? '',
    context.play.landingYardLine ?? '',
    context.play.fieldedYardLine ?? '',
    context.play.scramble ?? '',
    context.play.qbRun ?? '',
    context.play.readOptionKeep ?? '',
    context.direction,
    context.centerY,
  ].join(':');
}

export function routePlan(play: any, losX: number, centerY: number, endX: number, direction: number): { targetRole: string; routes: Record<string, [number, number]> } {
  const mediumX = clampAdvanceX(losX, endX, 78, 116, direction);
  const deepX = clampAdvanceX(losX, endX, 128, 168, direction);
  const bombX = clampAdvanceX(losX, endX, 168, 220, direction);
  const passFamily = resolvedPassFamily(play);
  const routeStyle = resolvedRouteStyle(play);
  const recordedTargetRole = play?.participants?.targetRole || play?.targetRole || (Array.isArray(play?.readOrder) ? play.readOrder[0] : null);

  switch (play.playId) {
    case 'quick-slant':
      return { targetRole: 'WR1', routes: { WR1: [mediumX, centerY - 48], WR2: [orientX(losX, 60, direction), centerY + 142], SLOT: [orientX(losX, 52, direction), centerY + 62], TE: [orientX(losX, 32, direction), centerY - 30], RB: [orientX(losX, -12, direction), centerY + 34] } };
    case 'slant-flat':
      return { targetRole: recordedTargetRole || 'WR1', routes: { WR1: [mediumX, centerY - 44], WR2: [orientX(losX, 66, direction), centerY + 134], SLOT: [orientX(losX, 56, direction), centerY + 60], TE: [orientX(losX, 26, direction), centerY - 18], RB: [orientX(losX, 20, direction), centerY + 28] } };
    case 'stick':
      return { targetRole: recordedTargetRole || 'TE', routes: { WR1: [orientX(losX, 74, direction), centerY - 122], WR2: [orientX(losX, 68, direction), centerY + 126], SLOT: [orientX(losX, 34, direction), centerY + 56], TE: [orientX(losX, 28, direction), centerY - 18], RB: [orientX(losX, 14, direction), centerY + 18] } };
    case 'spacing':
    case 'red-zone-spacing':
      return { targetRole: recordedTargetRole || 'SLOT', routes: { WR1: [orientX(losX, 38, direction), centerY - 92], WR2: [orientX(losX, 42, direction), centerY + 96], SLOT: [orientX(losX, 34, direction), centerY + 22], TE: [orientX(losX, 26, direction), centerY - 22], RB: [orientX(losX, 10, direction), centerY + 34] } };
    case 'mesh-cross':
      return { targetRole: 'SLOT', routes: { WR1: [mediumX, centerY + 6], WR2: [orientX(mediumX, 8, direction), centerY - 10], SLOT: [clampAdvanceX(losX, endX, 90, 120, direction), centerY + 34], TE: [orientX(losX, 54, direction), centerY - 58], RB: [orientX(losX, 8, direction), centerY + 46] } };
    case 'play-action-post':
      return { targetRole: 'WR1', routes: { WR1: [deepX, centerY - 82], WR2: [orientX(losX, 72, direction), centerY + 138], SLOT: [orientX(losX, 78, direction), centerY + 62], TE: [orientX(losX, 44, direction), centerY - 20], RB: [orientX(losX, -8, direction), centerY + 22] } };
    case 'flood-shot':
      return { targetRole: 'WR2', routes: { WR1: [orientX(losX, 92, direction), centerY - 132], WR2: [deepX, centerY + 118], SLOT: [mediumX, centerY + 64], TE: [orientX(losX, 52, direction), centerY + 18], RB: [orientX(losX, -6, direction), centerY + 26] } };
    case 'screen-left':
      return { targetRole: 'RB', routes: { WR1: [orientX(losX, 34, direction), centerY - 132], WR2: [orientX(losX, 46, direction), centerY + 134], SLOT: [orientX(losX, 38, direction), centerY + 92], TE: [orientX(losX, 26, direction), centerY - 74], RB: [clampAdvanceX(losX, endX, 54, 86, direction), centerY + 28] } };
    case 'wr-bubble-screen':
      return { targetRole: recordedTargetRole || 'SLOT', routes: { WR1: [orientX(losX, 18, direction), centerY - 130], WR2: [orientX(losX, 20, direction), centerY + 120], SLOT: [clampAdvanceX(losX, endX, 42, 72, direction), centerY + 56], TE: [orientX(losX, 18, direction), centerY - 52], RB: [orientX(losX, 6, direction), centerY + 26] } };
    case 'te-delay-screen':
      return { targetRole: recordedTargetRole || 'TE', routes: { WR1: [orientX(losX, 24, direction), centerY - 126], WR2: [orientX(losX, 30, direction), centerY + 128], SLOT: [orientX(losX, 24, direction), centerY + 62], TE: [clampAdvanceX(losX, endX, 52, 84, direction), centerY - 14], RB: [orientX(losX, 4, direction), centerY + 24] } };
    case 'hail-mary':
      return { targetRole: 'WR1', routes: { WR1: [bombX, centerY - 66], WR2: [orientX(bombX, -8, direction), centerY + 78], SLOT: [orientX(bombX, -18, direction), centerY + 8], TE: [orientX(losX, 84, direction), centerY - 18], RB: [orientX(losX, 18, direction), centerY + 44] } };
    default:
      break;
  }

  switch (passFamily) {
    case 'quick':
      return { targetRole: recordedTargetRole || 'WR1', routes: { WR1: [mediumX, centerY - 48], WR2: [orientX(losX, 60, direction), centerY + 130], SLOT: [orientX(losX, 52, direction), centerY + 58], TE: [orientX(losX, 34, direction), centerY - 24], RB: [orientX(losX, -10, direction), centerY + 30] } };
    case 'shallow':
    case 'mesh':
      return { targetRole: recordedTargetRole || 'SLOT', routes: { WR1: [mediumX, centerY + 4], WR2: [orientX(mediumX, 8, direction), centerY - 6], SLOT: [clampAdvanceX(losX, endX, 92, 122, direction), centerY + 30], TE: [orientX(losX, 58, direction), centerY - 54], RB: [orientX(losX, 8, direction), centerY + 44] } };
    case 'drive':
    case 'levels':
    case 'dig':
      return { targetRole: recordedTargetRole || 'WR1', routes: { WR1: [deepX, centerY - 52], WR2: [orientX(losX, 88, direction), centerY + 132], SLOT: [mediumX, centerY + 48], TE: [orientX(losX, 62, direction), centerY - 14], RB: [orientX(losX, 0, direction), centerY + 30] } };
    case 'smash':
      return { targetRole: recordedTargetRole || 'WR1', routes: { WR1: [deepX, centerY - 134], WR2: [deepX, centerY + 128], SLOT: [mediumX, centerY + 62], TE: [orientX(losX, 54, direction), centerY - 22], RB: [orientX(losX, -6, direction), centerY + 24] } };
    case 'flood':
    case 'boot':
      return { targetRole: recordedTargetRole || 'TE', routes: { WR1: [orientX(losX, 92, direction), centerY - 130], WR2: [deepX, centerY + 118], SLOT: [mediumX, centerY + 70], TE: [orientX(losX, 58, direction), centerY + 18], RB: [orientX(losX, 14, direction), centerY + 34] } };
    case 'screen':
      return { targetRole: recordedTargetRole || (play.screenType === 'wr' ? 'SLOT' : play.screenType === 'te' ? 'TE' : 'RB'), routes: { WR1: [orientX(losX, 34, direction), centerY - 132], WR2: [orientX(losX, 44, direction), centerY + 130], SLOT: [orientX(losX, 36, direction), centerY + 88], TE: [orientX(losX, 30, direction), centerY - 66], RB: [clampAdvanceX(losX, endX, 54, 86, direction), centerY + 28] } };
    case 'shot':
    case 'dagger':
      return { targetRole: recordedTargetRole || 'WR1', routes: { WR1: [deepX, centerY - 84], WR2: [orientX(losX, 82, direction), centerY + 134], SLOT: [orientX(losX, 78, direction), centerY + 60], TE: [orientX(losX, 48, direction), centerY - 18], RB: [orientX(losX, -6, direction), centerY + 22] } };
    case 'red-zone':
      return { targetRole: recordedTargetRole || 'WR1', routes: { WR1: [mediumX, centerY - 126], WR2: [orientX(losX, 62, direction), centerY + 122], SLOT: [orientX(losX, 42, direction), centerY + 56], TE: [orientX(losX, 30, direction), centerY - 18], RB: [orientX(losX, 0, direction), centerY + 18] } };
    case 'hail-mary':
      return { targetRole: recordedTargetRole || 'WR1', routes: { WR1: [bombX, centerY - 66], WR2: [orientX(bombX, -8, direction), centerY + 78], SLOT: [orientX(bombX, -18, direction), centerY + 8], TE: [orientX(losX, 84, direction), centerY - 18], RB: [orientX(losX, 18, direction), centerY + 44] } };
    default:
      break;
  }

  switch (routeStyle) {
    case 'spacing':
      return { targetRole: recordedTargetRole || 'SLOT', routes: { WR1: [orientX(losX, 38, direction), centerY - 92], WR2: [orientX(losX, 42, direction), centerY + 96], SLOT: [orientX(losX, 34, direction), centerY + 22], TE: [orientX(losX, 26, direction), centerY - 22], RB: [orientX(losX, 10, direction), centerY + 34] } };
    case 'stick':
      return { targetRole: recordedTargetRole || 'TE', routes: { WR1: [orientX(losX, 74, direction), centerY - 122], WR2: [orientX(losX, 68, direction), centerY + 126], SLOT: [orientX(losX, 34, direction), centerY + 56], TE: [orientX(losX, 28, direction), centerY - 18], RB: [orientX(losX, 14, direction), centerY + 18] } };
    case 'screen':
      return { targetRole: recordedTargetRole || (play.screenType === 'wr' ? 'SLOT' : play.screenType === 'te' ? 'TE' : 'RB'), routes: { WR1: [orientX(losX, 24, direction), centerY - 132], WR2: [orientX(losX, 28, direction), centerY + 132], SLOT: [orientX(losX, 24, direction), centerY + 84], TE: [orientX(losX, 22, direction), centerY - 58], RB: [clampAdvanceX(losX, endX, 48, 82, direction), centerY + 28] } };
    case 'vertical':
      return { targetRole: recordedTargetRole || 'WR1', routes: { WR1: [deepX, centerY - 84], WR2: [orientX(losX, 84, direction), centerY + 136], SLOT: [orientX(losX, 78, direction), centerY + 62], TE: [orientX(losX, 48, direction), centerY - 18], RB: [orientX(losX, -6, direction), centerY + 22] } };
    case 'flood':
      return { targetRole: recordedTargetRole || 'TE', routes: { WR1: [orientX(losX, 92, direction), centerY - 130], WR2: [deepX, centerY + 118], SLOT: [mediumX, centerY + 70], TE: [orientX(losX, 58, direction), centerY + 18], RB: [orientX(losX, 14, direction), centerY + 34] } };
    default:
      return { targetRole: recordedTargetRole || 'WR1', routes: { WR1: [mediumX, centerY - 62], WR2: [orientX(losX, 76, direction), centerY + 140], SLOT: [orientX(losX, 64, direction), centerY + 56], TE: [orientX(losX, 42, direction), centerY - 30], RB: [orientX(losX, -8, direction), centerY + 28] } };
  }
}
